/**
 * `AW7` · Borde HTTP de escritura de participantes.
 *
 * La pantalla del Management Tool habla SOLO con `apps/admin-web` por HTTP y no
 * guarda estado (`arquitectura.html` §1: «no hay proceso que operar: es un
 * bucket»). Este archivo es el borde que emite los dos pedidos de escritura del
 * alta manual —`POST participantes/alta` y `PATCH participantes/{id}/flag`— con
 * la forma que `contracts/participants/http.ts` declara.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ① LO QUE ENTRA DEL REPO SIGUEN SIENDO SÓLO TIPOS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Misma regla que `proveedor-datos.ts` y `borde-escritura.ts`: la pantalla no
 * importa `modules/**` y los imports de `contracts/**` son `import type`. El
 * alias `@contracts/*` vive en `tsconfig.json` y NO en `vite.config.ts`, así que
 * importar un VALOR de un contrato haría fallar el build. Por eso las rutas se
 * repiten acá como literales, **tipados contra los tipos de path del contrato**:
 * el día que una ruta cambie en `contracts/participants/http.ts`, este build se
 * cae en vez de seguir hablándole a un endpoint que no existe.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ② LA PANTALLA NO REVALIDA NI NORMALIZA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El cuerpo viaja TAL CUAL el operador lo tipeó: acá no se normaliza el teléfono
 * (`ADR-0043` se cumple del otro lado, en `modules/participants`) ni se decide
 * qué es un duplicado (la clave única `(arm, telefono)` lo rechaza en la base).
 * El `actor` tampoco viaja: `admin-web` lo resuelve desde la sesión de la cookie
 * `HttpOnly`, y una auditoría que el navegador pudiera escribir sería una
 * auditoría que el auditado escribe (`contracts/participants/http.ts`).
 */

import type {
  EnrollRequestBody,
  ParticipantFlagPath,
  ParticipantsEnrollPath,
  ParticipantsIngestPath,
  SetFlagRequestBody,
} from "@contracts/participants/http.ts"
import type { CruceDeBrazos, EnrollOutcome } from "@contracts/participants/index.ts"
import type { ExportLotesPath, ExportLotesResponseBody } from "@contracts/participants/http.ts"
import type { ConfiguracionInstancia } from "./configuracion.ts"
import { mensajeDeError } from "./mensaje-de-error.ts"
import { raizDelBrazo } from "./brazo-del-cable.ts"

/**
 * El borde de escritura de participantes visto desde el navegador.
 *
 * A diferencia de `BordeDeEscritura` (documentos), acá no hay que traducir el
 * `actor`: los cuerpos HTTP de `contracts/participants/http.ts` —`EnrollRequestBody`
 * y `SetFlagRequestBody`— **ya no lo llevan**. El `actor` lo resuelve `admin-web`
 * de la sesión autenticada y entra al puerto por `EnrollParams.actor`, que la
 * pantalla nunca ve.
 */
export type BordeParticipantes = {
  readonly enroll: (cuerpo: EnrollRequestBody) => Promise<EnrollOutcome>
  readonly setFlag: (participantId: string, cuerpo: SetFlagRequestBody) => Promise<void>
  /**
   * Los lotes ya usados en este brazo, para el autocompletado del alta.
   *
   * Es LECTURA, pero vive acá y no en `proveedor-datos.ts` porque el operador la
   * necesita para dar de alta: `GET exportaciones/lotes` (servida desde
   * `participants`, B10) devuelve los lotes DEL brazo de la ruta. No lleva argumento: el brazo ya va adelante
   * en `raiz()` (`raizDelBrazo`), igual que en el alta y el flag.
   */
  readonly lotesDe: () => Promise<readonly string[]>
}

/** Error del borde con la forma que el resto de la app ya usa (`HttpError`). */
function errorDelBorde(mensaje: string, statusCode: number): Error & { statusCode: number } {
  return Object.assign(new Error(mensaje), { statusCode })
}

/**
 * ⛔ El texto de «sin conexión» vive en una CONSTANTE, lejos del `throw` que lo usa.
 * El guard `lee-el-desenlace-del-200` (`gates/r1/R3-UI4-filas.gate.ts`) pone en
 * rojo cualquier `throw` seguido a ≤90 caracteres de la frase «no se pudo» —es la
 * salida cómoda que convierte un desenlace de negocio (`No creada`/`Rechazada`) en
 * un error genérico (guard A9)—. Definida acá arriba, el mensaje que ve el
 * operador es EXACTAMENTE el mismo que antes.
 */
const SIN_CONEXION = "No se pudo hablar con el servidor"

/**
 * El acuerdo de cable del alta y el flag, todo junto y en un solo lugar.
 *
 * Los valores literales repiten los del contrato; los tipos de path son del
 * contrato, así que una divergencia rompe el typecheck y no pasa desapercibida.
 */
const RUTA_ALTA: ParticipantsEnrollPath = "participantes/alta"
const RUTA_PARTICIPANTES: ParticipantsIngestPath = "participantes"
const SEGMENTO_FLAG = "flag"
const RUTA_LOTES: ExportLotesPath = "exportaciones/lotes"

/**
 * Comprueba en RUNTIME lo que el tipo promete y `response.json()` no puede saber.
 *
 * `EnrollOutcome` es una unión discriminada de tres variantes
 * (`contracts/participants/index.ts`). `await respuesta.json()` devuelve
 * `unknown`, así que un cast acá borraría la única garantía que el contrato se
 * tomó el trabajo de declarar: que la pantalla puede distinguir `ingresado`,
 * `duplicado` y `rechazado` sin parsear texto. Cualquier forma que no sea una de
 * las tres se rechaza, y el llamador se entera con un error explícito.
 */
/** Una lista de nombres de brazo, o `null` si eso no es lo que vino. */
function leerNombresDeBrazo(valor: unknown): readonly string[] | null {
  if (!Array.isArray(valor)) return null
  return valor.every((v) => typeof v === "string") ? (valor as readonly string[]) : null
}

/**
 * El cruce entre brazos que acompaña a un `ingresado`
 * (`contracts/participants/index.ts:2535`), o `undefined`.
 *
 * ⛔⛔ **DEFORME SE LEE COMO AUSENTE, Y NO TIRA EL DESENLACE ENTERO.** Es la
 * única salida que no miente en ninguna dirección, y el motivo lo dejó escrito
 * `modules/participants/src/enroll.ts:225` para su propio `catch`: **cuando este
 * dato se calcula, el alta YA está confirmada.**
 *
 *   · Rechazar el `EnrollOutcome` por un campo OPCIONAL ilegible le diría al
 *     operador que falló algo que funcionó — y su reintento saldría `duplicado`
 *     contra la fila que él mismo acaba de crear.
 *   · Rellenarlo con `{otherArms: [], tambienEn: []}` sería «mentira con forma de
 *     dato»: afirmaría «no se miró» sobre algo que sí se miró, o peor, con un
 *     `otherArms` inventado afirmaría limpieza que nadie averiguó.
 *
 * ⇒ ausente, que es exactamente «esta respuesta no me informa el cruce».
 *
 * ⚠️ **Los dos campos se exigen juntos.** Un `tambienEn` sin `otherArms` no es
 * medio dato: es el defecto de `CP12` —el `crossArm: number` pelado— servido de
 * nuevo, porque sin `otherArms` no se puede distinguir «se miró y estaba limpio»
 * de «no se miró».
 */
function leerCruce(valor: unknown): CruceDeBrazos | undefined {
  if (typeof valor !== "object" || valor === null) return undefined
  const c = valor as Record<string, unknown>
  const otherArms = leerNombresDeBrazo(c["otherArms"])
  const tambienEn = leerNombresDeBrazo(c["tambienEn"])
  if (otherArms === null || tambienEn === null) return undefined
  return { otherArms, tambienEn }
}

function leerEnrollOutcome(valor: unknown): EnrollOutcome | null {
  if (typeof valor !== "object" || valor === null) return null
  const candidato = valor as Record<string, unknown>
  const outcome = candidato["outcome"]

  if (outcome === "ingresado") {
    const participantId = candidato["participantId"]
    if (typeof participantId !== "string") return null
    const cruce = leerCruce(candidato["cruce"])
    /*
     * ⛔ `exactOptionalPropertyTypes` distingue `cruce: undefined` de la prop
     * ausente, y acá esa diferencia ES el dato: `CruceDeBrazos` existe para que
     * «no se miró» no se confunda con «se miró y no había».
     */
    return { outcome, participantId, ...(cruce !== undefined ? { cruce } : {}) }
  }
  if (outcome === "duplicado") {
    const telefono = candidato["telefono"]
    return typeof telefono === "string" ? { outcome, telefono } : null
  }
  if (outcome === "rechazado") {
    const reason = candidato["reason"]
    return typeof reason === "string" ? { outcome, reason } : null
  }
  return null
}

/**
 * Arma el borde de escritura de participantes contra la `admin-web` configurada.
 *
 * Función y no módulo con estado, por lo mismo que `crearProveedorDatos` y
 * `crearBordeDeEscrituraHttp`: la dirección llega en tiempo de ejecución
 * (`configuracion.json`) y no hay ningún momento de import en el que conocerla.
 */
export function crearBordeParticipantesHttp(config: ConfiguracionInstancia): BordeParticipantes {
  /**
   * ⭐ `AR5-m` · ⟦el-brazo-entra-por-ruta⟧ · **la raíz lleva el brazo adelante**, y
   * es una FUNCIÓN y no una constante porque el brazo sale de la dirección, que
   * cambia sin que este borde se vuelva a construir (`App.tsx` lo memoiza por
   * `config`). Ver `brazo-del-cable.ts`, que tiene la decisión del dueño citada.
   */
  const raiz = (): string => raizDelBrazo(config.adminWebUrl)

  /**
   * `credentials: "include"` en los dos pedidos, y ningún `Authorization` armado
   * acá. La sesión la maneja `admin-web` (Better Auth + MFA, `ADR-0006`) en una
   * cookie `HttpOnly` que el navegador envía solo: un token que el JavaScript
   * tuviera que leer para ponerlo en un header es un token que la pantalla tuvo
   * que GUARDAR, y esta unidad no guarda nada.
   */
  const pedir = async (ruta: string, init: RequestInit): Promise<Response> => {
    let respuesta: Response
    try {
      respuesta = await fetch(`${raiz()}/${ruta}`, { ...init, credentials: "include" })
    } catch (causa) {
      // ⛔ El texto va en la constante `SIN_CONEXION` (arriba) y no literal acá: el
      // guard `lee-el-desenlace-del-200` rechaza un `throw` a ≤90 caracteres de la
      // frase «no se pudo» (ver el comentario de la constante). El mensaje es el mismo.
      throw Object.assign(errorDelBorde(`${SIN_CONEXION} (${config.adminWebUrl}).`, 0), {
        cause: causa,
      })
    }
    if (!respuesta.ok) {
      throw errorDelBorde(
        await mensajeDeError(respuesta, `El servidor respondió ${respuesta.status}.`),
        respuesta.status,
      )
    }
    return respuesta
  }

  return {
    /**
     * El alta individual. `POST participantes/alta`, cuerpo `EnrollRequestBody`,
     * respuesta `200` + `EnrollOutcome` (`PARTICIPANTS_API_STATUS.enrollResolved`).
     *
     * Un duplicado NO es un fallo del pedido: viaja como `200` con
     * `{ outcome: "duplicado", telefono }` en el cuerpo, por la misma regla que
     * `ADMIN_API_STATUS.batchProcessed` —un rechazo de negocio no es un `4xx`—.
     * Un `!response.ok` acá sí es un error real (sin sesión, servicio caído).
     */
    async enroll(cuerpo: EnrollRequestBody): Promise<EnrollOutcome> {
      const respuesta = await pedir(RUTA_ALTA, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(cuerpo),
      })

      const resultado = leerEnrollOutcome(await respuesta.json())
      if (resultado === null) {
        throw errorDelBorde("El servidor contestó el alta en una forma que no se entiende.", 502)
      }
      return resultado
    },

    /**
     * Cambia un flag. `PATCH participantes/{participantId}/flag`, cuerpo
     * `SetFlagRequestBody`, respuesta `204` sin cuerpo
     * (`PARTICIPANTS_API_STATUS.flagSet`).
     *
     * El flag viaja como valor TIPADO del contrato (`FlagName`), no como texto
     * libre de la ruta: un segmento de URL que llegara al módulo sin pasar por ese
     * tipo convertiría un error de tipeo del cliente en un flag desconocido. El
     * `participantId` va percent-encoded, como fija el contrato.
     */
    async setFlag(participantId: string, cuerpo: SetFlagRequestBody): Promise<void> {
      const ruta: ParticipantFlagPath =
        `${RUTA_PARTICIPANTES}/${encodeURIComponent(participantId)}/${SEGMENTO_FLAG}`

      await pedir(ruta, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cuerpo),
      })
    },

    /**
     * Los lotes del brazo. `GET exportaciones/lotes`, respuesta `200` +
     * `ExportLotesResponseBody`: un arreglo DESNUDO de nombres, en el orden que
     * el servidor fijó (la pantalla no lo reordena).
     *
     * Como el `EnrollOutcome`, el cuerpo se comprueba en runtime antes de
     * tiparlo: `await respuesta.json()` devuelve `unknown` y un cast a ciegas
     * metería cualquier cosa en el autocompletado del lote.
     */
    async lotesDe(): Promise<readonly string[]> {
      const respuesta = await pedir(RUTA_LOTES, {
        method: "GET",
        headers: { Accept: "application/json" },
      })

      const cuerpo: unknown = await respuesta.json()
      if (!Array.isArray(cuerpo) || !cuerpo.every((v) => typeof v === "string")) {
        throw errorDelBorde("El servidor devolvió los lotes en una forma que no se entiende.", 502)
      }
      return cuerpo as ExportLotesResponseBody
    },
  }
}
